import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, Box, Copy, FileArchive, Hammer, Play, RefreshCw, Trash2, Upload } from 'lucide-react';
import { customAdminApi } from '../../../services/api';
import type { AdminHostAgent } from '../../../types/admin';
import type { CustomProduct, CustomProductImage, CustomProductRevision } from '../../../types/custom';
import { useSocket } from '../../../contexts/SocketContext';
import { customErrorMessage, formatBytes, newIdempotencyKey } from '../../../utils/customUi';
import { latestCustomProductRevision, selectedCustomProductRevision, type CustomRevisionSelection } from '../../../utils/customProductRevisions';
import { sha256File } from '../../../utils/sha256';
import { CopyCustomImageForm } from './CopyCustomImageForm';

interface UploadProgress {
  phase: 'hashing' | 'uploading' | 'completing';
  percent: number;
  message: string;
}

export function CustomImagesPanel({
  product,
  revisions,
  agents,
}: {
  product: CustomProduct;
  revisions: CustomProductRevision[];
  agents: AdminHostAgent[];
}) {
  const queryClient = useQueryClient();
  const { socket } = useSocket();
  const [version, setVersion] = useState('');
  const [revisionSelection, setRevisionSelection] = useState<CustomRevisionSelection | null>(null);
  const latestRevision = latestCustomProductRevision(revisions);
  const revision = selectedCustomProductRevision(product.id, revisions, revisionSelection);
  const [file, setFile] = useState<File | null>(null);
  const [resumeImage, setResumeImage] = useState<CustomProductImage | null>(null);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [selectedAgents, setSelectedAgents] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [copySourceId, setCopySourceId] = useState<string | null>(null);
  const [copySubmitting, setCopySubmitting] = useState(false);
  const resumeInput = useRef<HTMLInputElement>(null);

  const imagesQuery = useQuery({
    queryKey: ['custom-products', product.id, 'images'],
    queryFn: async () => (await customAdminApi.listImages(product.id)).images,
    refetchInterval: query => query.state.data?.some(image => (
      ['uploading', 'building', 'partially_ready', 'deleting'].includes(image.status)
      || image.replicas.some(replica => ['pending', 'building'].includes(replica.status))
    )) ? 2_000 : 10_000,
  });

  useEffect(() => {
    setRevisionSelection(null);
    setFile(null);
    setResumeImage(null);
  }, [product.id, latestRevision]);

  useEffect(() => {
    if (!socket) return;
    const refresh = (payload: { productId?: string }) => {
      if (!payload?.productId || payload.productId === product.id) {
        void queryClient.invalidateQueries({ queryKey: ['custom-products', product.id, 'images'] });
      }
    };
    socket.on('custom:image:progress', refresh);
    socket.on('custom:image:ready', refresh);
    socket.on('custom:image:failed', refresh);
    return () => {
      socket.off('custom:image:progress', refresh);
      socket.off('custom:image:ready', refresh);
      socket.off('custom:image:failed', refresh);
    };
  }, [product.id, queryClient, socket]);

  const eligibleAgents = useMemo(
    () => agents.filter(agent => agent.isActive && agent.connection.connected && agent.customWorkloadsEnabled),
    [agents],
  );

  const upload = async (selectedFile: File, existing?: CustomProductImage) => {
    setError('');
    setNotice('');
    setProgress({ phase: 'hashing', percent: 0, message: 'Calculating SHA-256 without loading the ZIP into memory…' });
    try {
      if (selectedFile.size < 1 || selectedFile.size > 5 * 1024 ** 3) {
        throw new Error('ZIP size must be between 1 byte and 5 GiB.');
      }
      const digest = await sha256File(selectedFile, (processed, total) => {
        setProgress({ phase: 'hashing', percent: total ? processed / total * 100 : 0, message: `Hashing ${formatBytes(processed)} / ${formatBytes(total)}` });
      });
      let image = existing;
      if (existing) {
        if (existing.artifact.sizeBytes !== selectedFile.size || existing.artifact.sha256 !== digest) {
          throw new Error('The selected file does not match this upload\'s pinned size and SHA-256.');
        }
      } else {
        if (!version || !revision) throw new Error('Choose a version and product revision.');
        const initiated = await customAdminApi.initiateImageUpload(product.id, {
          version,
          productRevision: revision,
          sha256: digest,
          sizeBytes: selectedFile.size,
          idempotencyKey: newIdempotencyKey('upload'),
        });
        image = initiated.image;
      }
      if (!image) throw new Error('Mothership did not return an image identity.');
      const uploadStatus = await customAdminApi.getImageUploadStatus(product.id, image.id);
      if (!uploadStatus.upload) {
        if (uploadStatus.image.artifact.status === 'uploaded') {
          setNotice(`Artifact ${uploadStatus.image.version} is already uploaded and verified.`);
          return;
        }
        throw new Error(uploadStatus.image.artifact.lastError || `Upload is not available in state ${uploadStatus.image.artifact.status}.`);
      }
      const completeParts = new Map(uploadStatus.parts.map(part => [part.partNumber, part.sizeBytes]));
      const missing = Array.from({ length: uploadStatus.upload.totalParts }, (_, index) => index + 1)
        .filter(partNumber => !completeParts.has(partNumber));
      let transferred = Array.from(completeParts.values()).reduce((sum, size) => sum + size, 0);
      for (let offset = 0; offset < missing.length; offset += 100) {
        const numbers = missing.slice(offset, offset + 100);
        const presigned = await customAdminApi.presignImageParts(product.id, image.id, numbers);
        for (const part of presigned.urls) {
          const start = (part.partNumber - 1) * uploadStatus.upload.partSizeBytes;
          const body = selectedFile.slice(start, start + part.sizeBytes);
          const response = await fetch(part.url, { method: 'PUT', body });
          if (!response.ok) throw new Error(`Object storage rejected part ${part.partNumber} (HTTP ${response.status}).`);
          transferred += part.sizeBytes;
          setProgress({ phase: 'uploading', percent: transferred / selectedFile.size * 100, message: `Uploaded part ${part.partNumber}/${uploadStatus.upload.totalParts} · ${formatBytes(transferred)} / ${formatBytes(selectedFile.size)}` });
        }
      }
      setProgress({ phase: 'completing', percent: 100, message: 'Finalizing multipart upload and requesting streaming validation…' });
      await customAdminApi.completeImageUpload(product.id, image.id, newIdempotencyKey('complete-upload'));
      setNotice(`Upload ${image.version} completed. Mothership is validating SHA-256, size and required ZIP paths.`);
      setVersion('');
      setRevisionSelection(null);
      setFile(null);
      setResumeImage(null);
      await queryClient.invalidateQueries({ queryKey: ['custom-products', product.id, 'images'] });
    } catch (caught) {
      setError(customErrorMessage(caught, 'The artifact upload failed. Select the same file and use Resume upload.'));
    } finally {
      setProgress(null);
    }
  };

  const build = async (image: CustomProductImage) => {
    const availableAgents = eligibleAgents.filter(agent => (
      !image.replicas.some(replica => replica.agentId === agent._id)
    ));
    const requestedAgent = selectedAgents[image.id];
    const agentId = availableAgents.some(agent => agent._id === requestedAgent)
      ? requestedAgent
      : availableAgents[0]?._id;
    if (!agentId) {
      setError('No eligible Agent is active, connected and opted in for custom workloads.');
      return;
    }
    setBusy(`build:${image.id}`);
    setError('');
    try {
      const key = newIdempotencyKey('image-build');
      const result = await customAdminApi.requestImageBuild(product.id, image.id, agentId, key);
      localStorage.setItem(`custom-build-key:${result.operationId}`, key);
      setNotice(`Build ${result.operationId} accepted. Progress and logs are recovered from Mothership.`);
      await queryClient.invalidateQueries({ queryKey: ['custom-products', product.id, 'images'] });
    } catch (caught) {
      setError(customErrorMessage(caught, 'The image build could not be started.'));
    } finally {
      setBusy(null);
    }
  };

  const retryBuild = async (image: CustomProductImage, operationId: string, agentId: string) => {
    const key = localStorage.getItem(`custom-build-key:${operationId}`);
    if (!key) {
      setError('This browser does not have the original retry key. The durable reconciler will retry automatically at nextRetryAt.');
      return;
    }
    setBusy(`retry:${operationId}`);
    try {
      await customAdminApi.requestImageBuild(product.id, image.id, agentId, key);
      setNotice('Build retry was requested with the original idempotency key.');
      await queryClient.invalidateQueries({ queryKey: ['custom-products', product.id, 'images'] });
    } catch (caught) {
      setError(customErrorMessage(caught, 'The build retry could not be requested.'));
    } finally {
      setBusy(null);
    }
  };

  const mutateImage = async (image: CustomProductImage, action: 'retire' | 'delete') => {
    setBusy(`${action}:${image.id}`);
    setError('');
    try {
      if (action === 'retire') await customAdminApi.retireImage(product.id, image.id);
      else await customAdminApi.deleteImage(product.id, image.id, newIdempotencyKey('image-delete'));
      setNotice(action === 'retire'
        ? 'Image retired. Existing pinned instances are unchanged.'
        : 'Image deletion accepted. Agent replicas are being cleaned up first.');
      await queryClient.invalidateQueries({ queryKey: ['custom-products', product.id, 'images'] });
    } catch (caught) {
      setError(customErrorMessage(caught, `The image could not be ${action}d.`));
    } finally {
      setBusy(null);
    }
  };

  const images = imagesQuery.data || [];
  return <div className="space-y-5">
    <div className="card p-5">
      <div className="flex flex-wrap justify-between gap-4 items-start">
        <div><h3 className="font-semibold text-gray-200 flex items-center gap-2"><Upload className="w-4 h-4 text-neon-400" /> Upload immutable image artifact</h3><p className="text-xs text-gray-500 mt-1">The ZIP is hashed and uploaded in 64 MiB presigned parts. Interrupted uploads can resume.</p></div>
        <div className="text-xs text-gray-500">Maximum 5 GiB · incomplete uploads expire after 24h</div>
      </div>
      <div className="grid md:grid-cols-[1fr_1fr_2fr_auto] gap-3 mt-4 items-end">
        <label className="text-xs text-gray-500">Version<input className="input mt-1" value={version} placeholder="v1.0.0" onChange={event => setVersion(event.target.value)} disabled={Boolean(progress)} /></label>
        <label className="text-xs text-gray-500">Product revision<select className="input mt-1" value={revision} onChange={event => setRevisionSelection({ productId: product.id, latestRevision, revision: Number(event.target.value) })} disabled={Boolean(progress)}>{revisions.map(item => <option key={item.id} value={item.revision}>r{item.revision} · {item.contentHash.slice(0, 10)}</option>)}</select></label>
        <label className="text-xs text-gray-500">ZIP artifact<input className="input mt-1 pt-2" type="file" accept=".zip,application/zip" onChange={event => setFile(event.target.files?.[0] || null)} disabled={Boolean(progress)} /></label>
        <button className="btn-primary" disabled={!file || !version || !revision || Boolean(progress)} onClick={() => file && void upload(file)}><Upload className="w-4 h-4" /> Upload</button>
      </div>
      {progress && <div className="mt-4"><div className="flex justify-between text-xs text-gray-400"><span>{progress.message}</span><span>{Math.round(progress.percent)}%</span></div><div className="progress-bar mt-2"><div className="progress-fill" style={{ width: `${progress.percent}%` }} /></div></div>}
    </div>

    {error && <div className="p-3 rounded border border-red-500/30 bg-red-500/10 text-sm text-red-300">{error}</div>}
    {notice && <div className="p-3 rounded border border-neon-500/20 bg-neon-500/10 text-sm text-neon-300">{notice}</div>}
    {imagesQuery.isError && <div className="p-4 text-red-300">{customErrorMessage(imagesQuery.error, 'Images could not be loaded.')}</div>}
    {imagesQuery.isLoading && <div className="spinner w-8 h-8 mx-auto" />}
    {!imagesQuery.isLoading && images.length === 0 && <div className="empty-state py-10"><FileArchive className="empty-state-icon" /><div className="empty-state-title">No image releases</div><p className="empty-state-text">Upload the first ZIP against an immutable product revision.</p></div>}

    {images.map(image => {
      const buildAgents = eligibleAgents.filter(agent => (
        !image.replicas.some(replica => replica.agentId === agent._id)
      ));
      const buildAgentId = buildAgents.some(agent => agent._id === selectedAgents[image.id])
        ? selectedAgents[image.id]
        : (buildAgents[0]?._id || '');
      const canCopy = image.artifact.status === 'uploaded' && image.status !== 'deleting'
        && Boolean(image.artifact.verifiedSha256) && image.artifact.verifiedSizeBytes === image.artifact.sizeBytes;
      const artifactPending = ['copying', 'validating'].includes(image.artifact.status);
      return <div className="card overflow-hidden" key={image.id}>
      <div className="p-4 flex flex-wrap justify-between gap-4 border-b border-dark-600">
        <div><div className="flex items-center gap-2"><Box className="w-4 h-4 text-cyber-400" /><span className="font-semibold text-gray-200">{image.version}</span><StatusBadge status={artifactPending ? image.artifact.status : image.status} /></div><div className="text-xs text-gray-500 mt-1">revision {image.productRevision} · {formatBytes(image.artifact.sizeBytes)} · SHA {image.artifact.sha256.slice(0, 12)}</div></div>
        <div className="flex flex-wrap gap-2">
          {canCopy && <button className="btn-secondary" disabled={Boolean(busy) || copySubmitting} aria-expanded={copySourceId === image.id} onClick={() => setCopySourceId(image.id)}><Copy className="w-4 h-4" /> Creează din același ZIP</button>}
          {image.artifact.status === 'uploading' && <><input ref={resumeInput} className="hidden" type="file" accept=".zip,application/zip" onChange={event => { const selected = event.target.files?.[0]; if (selected && resumeImage) void upload(selected, resumeImage); event.currentTarget.value = ''; }} /><button className="btn-secondary" disabled={Boolean(progress)} onClick={() => { setResumeImage(image); setTimeout(() => resumeInput.current?.click(), 0); }}><RefreshCw className="w-4 h-4" /> Resume upload</button></>}
          {image.artifact.status === 'uploaded' && !['retired', 'deleting'].includes(image.status) && <>{buildAgents.length ? <><select aria-label={`Build Agent for ${image.version}`} className="input w-56" value={buildAgentId} onChange={event => setSelectedAgents(value => ({ ...value, [image.id]: event.target.value }))}>{buildAgents.map(agent => <option key={agent._id} value={agent._id}>{agent.name} · {agent.region || agent.ipAddress}</option>)}</select><button className="btn-primary" disabled={busy === `build:${image.id}`} onClick={() => void build(image)}><Hammer className="w-4 h-4" /> Build</button></> : <span className="badge-info">Every eligible Agent already has a replica</span>}</>}
          {['uploaded', 'partially_ready', 'ready'].includes(image.status) && <button className="btn-secondary" disabled={Boolean(busy)} onClick={() => void mutateImage(image, 'retire')}><Archive className="w-4 h-4" /> Retire</button>}
          <button className="btn-danger" disabled={Boolean(busy) || copySubmitting || image.status === 'deleting' || artifactPending} title={artifactPending ? 'Wait for ZIP copying and validation to finish before deleting this release' : 'Deletion cleans Agent replicas automatically and is refused while instances reference this release'} onClick={() => confirm(`Delete immutable image ${image.version}? Agent replicas will be cleaned automatically; pinned instances still block deletion.`) && void mutateImage(image, 'delete')}><Trash2 className="w-4 h-4" /> {image.status === 'deleting' ? 'Deleting…' : (image.status === 'failed' && image.replicas.length === 0 ? 'Delete failed release' : 'Delete')}</button>
        </div>
      </div>
      {canCopy && copySourceId === image.id && <CopyCustomImageForm key={image.id} source={image} revisions={revisions} images={images} onSubmittingChange={setCopySubmitting} onClose={() => setCopySourceId(null)} onCreated={created => {
        setCopySourceId(null);
        setError('');
        setNotice(`Release ${created.version} accepted for revision ${created.productRevision}. Its ZIP is copied and validated before Build becomes available.`);
        queryClient.setQueryData<CustomProductImage[]>(['custom-products', product.id, 'images'], current => [created, ...(current || []).filter(item => item.id !== created.id)]);
        void queryClient.invalidateQueries({ queryKey: ['custom-products', product.id, 'images'] });
      }} />}
      {artifactPending && <p role="status" className="mx-4 mt-4 text-sm text-cyber-300 flex items-start gap-2"><RefreshCw className="w-4 h-4 animate-spin shrink-0 mt-0.5" />{image.artifact.status === 'copying' ? 'Copying the existing ZIP to independent storage. This continues if you leave the page.' : `Validating ZIP contents, checksum and required paths for revision ${image.productRevision}. Build will be available when validation succeeds.`}</p>}
      {image.artifact.lastError && <div className="mx-4 mt-4 p-3 rounded bg-red-500/10 border border-red-500/20 text-xs text-red-300">Artifact: {image.artifact.lastError}</div>}
      <div className="p-4 space-y-3">
        {image.replicas.length === 0 ? <p className="text-sm text-gray-500">No Agent replica has been requested.</p> : image.replicas.map(replica => {
          const agent = agents.find(candidate => candidate._id === replica.agentId);
          const storedRetryKey = replica.operationId && localStorage.getItem(`custom-build-key:${replica.operationId}`);
          return <div className="rounded-lg border border-dark-600 bg-dark-900/40 p-3" key={replica.replicaId}>
            <div className="flex flex-wrap justify-between gap-3">
              <div><div className="flex items-center gap-2 text-sm"><span className="font-medium text-gray-200">{agent?.name || replica.agentId}</span><StatusBadge status={replica.status} /></div><div className="text-xs text-gray-500 mt-1">{replica.phase || 'requested'} · {replica.status === 'deleting' ? `cleanup attempt ${replica.deleteAttempts}` : `attempt ${replica.attempts}`}{replica.imageTag ? ` · ${replica.imageTag}` : ''}</div></div>
              <div className="flex items-center gap-2">{replica.retryable && replica.status === 'failed' && <button className="btn-secondary py-1.5" disabled={!storedRetryKey || busy === `retry:${replica.operationId}`} title={storedRetryKey ? 'Replay the original idempotent build intent' : 'Automatic retry remains active; original key is not in this browser'} onClick={() => replica.operationId && void retryBuild(image, replica.operationId, replica.agentId)}><RefreshCw className="w-4 h-4" /> Retry</button>}{replica.status === 'ready' && <Play className="w-4 h-4 text-neon-400" />}</div>
            </div>
            {['pending', 'building'].includes(replica.status) && <div className="mt-3"><div className="progress-bar"><div className="progress-fill" style={{ width: `${replica.progressPercent}%` }} /></div><div className="text-xs text-gray-500 text-right mt-1">{replica.progressPercent}%</div></div>}
            {replica.lastError && <div className="mt-3 text-xs text-red-300">{replica.lastError}{replica.nextRetryAt ? ` · retry ${new Date(replica.nextRetryAt).toLocaleString()}` : ''}</div>}
            {replica.deleteLastError && <div className="mt-3 text-xs text-red-300">Cleanup: {replica.deleteLastError}{replica.deleteNextRetryAt ? ` · retry ${new Date(replica.deleteNextRetryAt).toLocaleString()}` : ''}</div>}
            {replica.dockerImageId && <div className="mt-2 text-xs text-gray-500 font-mono break-all">image {replica.dockerImageId}{replica.dockerRepoDigest ? ` · ${replica.dockerRepoDigest}` : ''}</div>}
            {replica.logs.length > 0 && <details className="mt-3"><summary className="text-xs text-cyber-400 cursor-pointer">Build logs ({replica.logs.length})</summary><div className="mt-2 max-h-52 overflow-auto bg-black/40 rounded p-3 font-mono text-xs space-y-1">{replica.logs.map((entry, index) => <div key={`${entry.at}-${index}`}><span className="text-gray-600">{new Date(entry.at).toLocaleTimeString()} · {entry.percent}% · {entry.phase}</span> <span className="text-gray-300">{entry.message}</span></div>)}</div></details>}
          </div>;
        })}
      </div>
    </div>;
    })}
  </div>;
}

function StatusBadge({ status }: { status: string }) {
  const className = ['ready', 'uploaded', 'running', 'active'].includes(status)
    ? 'badge-online'
    : (['failed', 'error', 'expired'].includes(status) ? 'badge-error' : (['building', 'uploading', 'copying', 'validating', 'pending', 'deleting'].includes(status) ? 'badge-warning' : 'badge-info'));
  return <span className={className}>{status.replaceAll('_', ' ')}</span>;
}
